import { useContext, useEffect, useMemo, useRef, useState } from 'react';
import {
  FieldPath,
  SubmitErrorHandler,
  SubmitHandler,
  useForm,
  useWatch,
} from 'react-hook-form';

import { SidebarStateContext } from 'contexts/sidebarStateContext';
import { useGuardedCreateLoad } from 'hooks/useGuardedCreateLoad';
import { useLoadContext } from 'hooks/useLoadContext';
import { useToast } from 'hooks/useToaster';
import { updateTMS } from 'lib/api/updateTMS';
import { FormStorageService } from 'lib/services/FormStorage/service';
import { SavedFormState } from 'lib/services/FormStorage/types';
import { useLoadBuildingAccordionTabs } from 'pages/QuoteView/LoadBuilding/hooks/useLoadBuildingAccordionTabs';
import { Load } from 'types/Load';
import { Maybe, Undef } from 'types/UtilityTypes';
import { TMSOperation, UpdateTMSResult } from 'types/api/UpdateTMS';
import { TMS } from 'types/enums/Integrations';
import { SuggestionPipelines } from 'types/suggestions/CoreSuggestions';
import { SuggestedLoad } from 'types/suggestions/LoadBuildingSuggestions';
import {
  acceptLoadViewSuggestion,
  useApplyLoadViewSuggestion,
  useFocusLoadViewSuggestion,
} from 'utils/applyLoadViewSuggestion';
import { applyCompleteKitSuggestions } from 'utils/completeKitSuggestion';
import { createLoadDateOnlyMetadata } from 'utils/dateOnlyFormValue';
import {
  DEFAULT_MAX_VALIDATION_TOAST_LABELS,
  ManualFieldError,
  applyManualValidationErrors,
  handleFormErrors,
  loadBuildingManualValidationDisplayOptions,
  scheduleLoadBuildingSuggestionValidation,
} from 'utils/formValidation';
import {
  normalizeToArray,
  normalizeToStringArray,
  withLoadBuildingSuggestionContext,
} from 'utils/loadInfoAndBuilding';

import { validateDuplicateRouteStopLocations } from '../../utils/stopValidation';
import { ALL_PLS_TABS, FORM_RESET_OPTIONS } from '../constants';
import {
  ensurePlsRouteStops,
  isPlsDropoffStopType,
  isPlsMultiStopRoute,
  isPlsPickupStopType,
  normalizePlsRouteStopTypes,
  syncPlsRouteStopsToPickupConsignee,
  validatePlsMonotonicRouteStopTimes,
} from '../plsRouteStops';
import { PlsFormValues } from '../types';
import {
  buildPlsCreateLoadPayload,
  createDefaultPlsCommodity,
  getPlsCreateLoadFieldErrors,
  hasCompletePlsCommodity,
  hydratePlsFormRouteStops,
  initPlsLoadForm,
  isValidPlsDateTimeWindow,
  mapBuiltLoadToPlsFormValues,
  mapPlsSuggestionToFormValues,
  parsePlsSuggestionWallClock,
  partitionPlsReferenceFieldsByOperationalCodes,
} from '../utils';
import {
  validatePlsCreateLoad,
  validatePlsPendingReferences,
  validatePlsRequiredCustomerReferences,
} from '../utils/plsCreateValidation';
import { usePlsData } from './usePlsData';
import {
  usePlsOperationalFields,
  usePlsReferenceCatalogs,
} from './usePlsReferenceCatalogs';

async function refetchLoadFromContext(
  invalidateLoad: ReturnType<typeof useLoadContext>['invalidateLoad'],
  result?: Pick<UpdateTMSResult, 'load' | 'loadAttributes'>
) {
  if (result?.load && result?.loadAttributes) {
    await invalidateLoad(result.load, result.loadAttributes);
  } else {
    await invalidateLoad();
  }
}

interface UsePlsFormLogicParams {
  isCreateMode: boolean;
  load?: Load;
  tmsID?: number;
  threadId?: string;
  emailId?: number;
  threadItemId?: string;
}

export function usePlsFormLogic({
  isCreateMode,
  load,
  tmsID,
  threadId = '',
  emailId,
  threadItemId,
}: UsePlsFormLogicParams) {
  const { toast } = useToast();
  const { invalidateLoad } = useLoadContext();
  const guardedCreateLoad = useGuardedCreateLoad();
  const tmsName = TMS.PLS;

  const {
    currentState: { clickedSuggestion, goToSuggestion },
    setCurrentState,
  } = useContext(SidebarStateContext);
  const clickedSuggestionRef = useRef(clickedSuggestion);
  clickedSuggestionRef.current = clickedSuggestion;

  const [isLoading, setIsLoading] = useState(false);
  const [builtLoad, setBuiltLoad] = useState<Maybe<Load>>(null);

  const {
    activeTabs,
    setActiveTabs,
    expandAll,
    collapseAll,
    defaultView,
    setDefaultView,
  } = useLoadBuildingAccordionTabs({ allTabs: ALL_PLS_TABS });

  const isLoadBuildingSuggestionClicked = Boolean(
    isCreateMode &&
    clickedSuggestion?.pipeline === SuggestionPipelines.LoadBuilding
  );
  const showAIHints = isLoadBuildingSuggestionClicked;
  const formStorageKey = `${tmsName}_${threadId}`;
  const draftContextKey = isCreateMode && threadId ? formStorageKey : '';
  const restoredDraftKeyRef = useRef<Undef<string>>(undefined);
  const explicitlyClearedKeyRef = useRef<Undef<string>>(undefined);
  const loadedDraftContextKeyRef = useRef(draftContextKey);
  const [savedDraft, setSavedDraft] = useState<{
    key: string;
    state: Undef<SavedFormState<PlsFormValues, unknown>>;
  }>(() => ({
    key: draftContextKey,
    state: draftContextKey
      ? FormStorageService.getFormState<PlsFormValues, unknown>(formStorageKey)
      : undefined,
  }));
  const isDraftContextLoaded = savedDraft.key === draftContextKey;
  const savedFormState = isDraftContextLoaded ? savedDraft.state : undefined;

  useEffect(() => {
    if (loadedDraftContextKeyRef.current === draftContextKey) {
      return;
    }

    loadedDraftContextKeyRef.current = draftContextKey;
    restoredDraftKeyRef.current = undefined;
    explicitlyClearedKeyRef.current = undefined;
    setSavedDraft({
      key: draftContextKey,
      state: draftContextKey
        ? FormStorageService.getFormState<PlsFormValues, unknown>(
            formStorageKey
          )
        : undefined,
    });
  }, [draftContextKey, formStorageKey]);

  const hasRestorableSavedState = Boolean(
    savedFormState &&
    savedFormState.threadID === threadId &&
    clickedSuggestion == null &&
    !builtLoad
  );

  useEffect(() => {
    if (
      isCreateMode &&
      !clickedSuggestion &&
      isDraftContextLoaded &&
      !hasRestorableSavedState &&
      explicitlyClearedKeyRef.current !== formStorageKey
    ) {
      void goToSuggestion({
        suggestionPipeline: SuggestionPipelines.LoadBuilding,
      });
    }
  }, [
    isCreateMode,
    clickedSuggestion,
    goToSuggestion,
    hasRestorableSavedState,
    formStorageKey,
    isDraftContextLoaded,
  ]);

  useEffect(() => {
    if (clickedSuggestion) {
      explicitlyClearedKeyRef.current = undefined;
    }
  }, [clickedSuggestion]);

  // Key create-mode defaults on suggestion id, not object identity — Kit/SharePoint
  // rebuilds replace the same card and would wipe in-progress edits via form reset.
  const createModeSuggestionId = isCreateMode ? clickedSuggestion?.id : null;

  const memoizedDefaultValues: PlsFormValues = useMemo(() => {
    if (builtLoad) {
      return mapBuiltLoadToPlsFormValues(builtLoad);
    }

    if (!isCreateMode && load) {
      return mapBuiltLoadToPlsFormValues(load);
    }

    const baseForm = initPlsLoadForm();
    const activeSuggestion = clickedSuggestionRef.current;

    if (
      createModeSuggestionId == null ||
      activeSuggestion?.pipeline !== SuggestionPipelines.LoadBuilding
    ) {
      return baseForm;
    }

    const casted = activeSuggestion.suggested as SuggestedLoad;
    const suggestedCommodities = normalizeToArray(casted.commodities) ?? [];
    const normalizeSuggestionStopDate = (value: unknown): string => {
      const parsed = parsePlsSuggestionWallClock(value);
      return parsed?.toISOString() ?? '';
    };
    const suggestedStops = (normalizeToArray(casted.stops) ?? []).map(
      (stop) => ({
        ...stop,
        readyTime: normalizeSuggestionStopDate(stop.readyTime),
        readyEndTime: normalizeSuggestionStopDate(stop.readyEndTime),
        mustDeliver: normalizeSuggestionStopDate(stop.mustDeliver),
        apptStartTime: normalizeSuggestionStopDate(stop.apptStartTime),
        apptEndTime: normalizeSuggestionStopDate(stop.apptEndTime),
      })
    );

    const suggestedFields = mapPlsSuggestionToFormValues({
      ...casted,
      customer: {
        ...casted.customer,
        refNumberCandidates: normalizeToStringArray(
          casted.customer?.refNumberCandidates
        ),
      },
      pickup: {
        ...casted.pickup,
        readyTime: parsePlsSuggestionWallClock(casted.pickup?.readyTime),
        readyTimeEnd: parsePlsSuggestionWallClock(casted.pickup?.readyTimeEnd),
        apptStartTime: parsePlsSuggestionWallClock(
          casted.pickup?.apptStartTime
        ),
        apptEndTime: parsePlsSuggestionWallClock(casted.pickup?.apptEndTime),
        refNumberCandidates: normalizeToStringArray(
          casted.pickup?.refNumberCandidates
        ),
      },
      consignee: {
        ...casted.consignee,
        mustDeliver: parsePlsSuggestionWallClock(casted.consignee?.mustDeliver),
        mustDeliverEnd: parsePlsSuggestionWallClock(
          casted.consignee?.mustDeliverEnd
        ),
        apptStartTime: parsePlsSuggestionWallClock(
          casted.consignee?.apptStartTime
        ),
        apptEndTime: parsePlsSuggestionWallClock(casted.consignee?.apptEndTime),
        refNumberCandidates: normalizeToStringArray(
          casted.consignee?.refNumberCandidates
        ),
      },
      stops: suggestedStops,
      commodities:
        suggestedCommodities.length > 0
          ? suggestedCommodities
          : [createDefaultPlsCommodity()],
    });

    return {
      ...baseForm,
      ...suggestedFields,
    } as PlsFormValues;
  }, [builtLoad, isCreateMode, load, createModeSuggestionId]);

  const formMethods = useForm<PlsFormValues>({
    defaultValues: memoizedDefaultValues,
  });

  const watchedCustomerId =
    useWatch({
      control: formMethods.control,
      name: 'customer.externalTMSID',
    }) ?? '';
  const watchedBillToId =
    useWatch({
      control: formMethods.control,
      name: 'billTo.externalTMSID',
    }) ?? '';

  const plsData = usePlsData(
    tmsID,
    watchedCustomerId,
    hasRestorableSavedState
      ? savedFormState?.values.customer
      : memoizedDefaultValues.customer
  );
  const {
    fieldDefinitions,
    isLoading: isLoadingOperationalFields,
    error: operationalFieldsError,
  } = usePlsOperationalFields(tmsID, watchedBillToId);
  const referenceCatalogs = usePlsReferenceCatalogs(tmsID);

  useEffect(() => {
    if (isCreateMode && threadId && !isDraftContextLoaded) {
      return;
    }

    if (hasRestorableSavedState && savedFormState) {
      if (restoredDraftKeyRef.current === formStorageKey) {
        return;
      }

      restoredDraftKeyRef.current = formStorageKey;
      formMethods.reset(hydratePlsFormRouteStops(savedFormState.values), {
        ...FORM_RESET_OPTIONS,
        keepDefaultValues: true,
      });
      return;
    }

    formMethods.reset(memoizedDefaultValues, FORM_RESET_OPTIONS);
    if (builtLoad && isCreateMode && threadId) {
      FormStorageService.clearFormState(formStorageKey);
    }

    return scheduleLoadBuildingSuggestionValidation(
      isLoadBuildingSuggestionClicked && !builtLoad,
      () => {
        void formMethods.trigger();
      }
    );
  }, [
    hasRestorableSavedState,
    savedFormState,
    formStorageKey,
    memoizedDefaultValues,
    formMethods,
    builtLoad,
    isCreateMode,
    threadId,
    isDraftContextLoaded,
    isLoadBuildingSuggestionClicked,
  ]);

  // Partition load ↔ Customer Reference Fields against the current bill-to
  // ops catalog. Bidirectional so a stale catalog from a prior suggestion /
  // bill-to cannot leave codes stranded in plsOperationalFields after switch.
  // Declared after the reset effect so getValues() sees freshly hydrated refs.
  useEffect(() => {
    const operationalCodes = fieldDefinitions.map(
      (definition) => definition.code
    );

    const currentOperational =
      formMethods.getValues('plsOperationalFields') ?? {};
    const currentLoadRefs =
      formMethods.getValues('plsLoadReferenceFields') ?? {};
    const partitioned = partitionPlsReferenceFieldsByOperationalCodes(
      {
        plsOperationalFields: currentOperational,
        plsLoadReferenceFields: currentLoadRefs,
      },
      operationalCodes
    );

    if (!partitioned.changed) {
      return;
    }

    const movedLoadRefQualifiers = Object.keys(currentLoadRefs).filter(
      (code) => !(code in partitioned.plsLoadReferenceFields)
    );

    formMethods.setValue(
      'plsOperationalFields',
      partitioned.plsOperationalFields
    );
    formMethods.setValue(
      'plsLoadReferenceFields',
      partitioned.plsLoadReferenceFields
    );

    // Drop load→ops moved qualifiers from additionalReferences so submit
    // cannot re-send the pre-partition spelling alongside the catalog code.
    if (movedLoadRefQualifiers.length > 0) {
      const moved = new Set(movedLoadRefQualifiers);
      formMethods.setValue(
        'additionalReferences',
        (
          normalizeToArray(formMethods.getValues('additionalReferences')) ?? []
        ).filter((reference) => !moved.has(reference.qualifier))
      );
    }
  }, [createModeSuggestionId, fieldDefinitions, formMethods]);

  useApplyLoadViewSuggestion(
    formMethods.setValue,
    clickedSuggestion,
    undefined,
    !isCreateMode,
    memoizedDefaultValues
  );

  useFocusLoadViewSuggestion(
    clickedSuggestion,
    undefined,
    setActiveTabs,
    !isCreateMode,
    ALL_PLS_TABS
  );

  useEffect(() => {
    if (!isCreateMode || !threadId || !isDraftContextLoaded || builtLoad) {
      return;
    }

    const subscription = formMethods.watch((value) => {
      FormStorageService.saveFormState(formStorageKey, {
        threadID: threadId,
        values: value,
        clickedSuggestion: isLoadBuildingSuggestionClicked
          ? clickedSuggestion
          : null,
        dirtyFields: formMethods.formState.dirtyFields,
      });
    });

    return () => subscription.unsubscribe();
  }, [
    isCreateMode,
    formMethods,
    threadId,
    formStorageKey,
    isLoadBuildingSuggestionClicked,
    clickedSuggestion,
    builtLoad,
    isDraftContextLoaded,
  ]);

  const applySubmitValidationErrors = (
    fieldErrors: ManualFieldError<PlsFormValues>[]
  ) =>
    applyManualValidationErrors(
      formMethods,
      fieldErrors,
      loadBuildingManualValidationDisplayOptions({
        tmsName: TMS.PLS,
        activeTabs,
        setActiveTabs,
        toast,
        routeStopCount: (formMethods.getValues('stops') ?? []).length,
        afterErrorHandled: () => setIsLoading(false),
      })
    );

  const onSubmit: SubmitHandler<PlsFormValues> = async (data) => {
    setIsLoading(true);
    formMethods.clearErrors();

    if (
      operationalFieldsError ||
      referenceCatalogs.errors.loadReferenceFields
    ) {
      toast({
        description: isCreateMode
          ? 'PLS reference fields could not be loaded. Retry before building the load.'
          : 'PLS reference fields could not be loaded. Retry before updating the load.',
        variant: 'destructive',
      });
      setIsLoading(false);
      return;
    }

    const normalizedRouteStops = normalizePlsRouteStopTypes(
      ensurePlsRouteStops(data)
    );
    const syncedEnds = syncPlsRouteStopsToPickupConsignee(normalizedRouteStops);

    if (normalizedRouteStops.length < 2) {
      applySubmitValidationErrors([
        {
          path: 'stops.0.address.addressLine1',
          message: 'Please provide at least a pickup and a consignee.',
        },
      ]);
      return;
    }

    // Create and update both strip a card that has no type, so a number on one stops the save.
    const pendingErrors = validatePlsPendingReferences(
      data.plsPendingReferences
    );
    if (pendingErrors.length > 0) {
      applySubmitValidationErrors(pendingErrors);
      return;
    }

    if (isCreateMode) {
      const createErrors = [
        ...validatePlsRequiredCustomerReferences(
          data.plsOperationalFields,
          fieldDefinitions,
          Boolean(watchedBillToId) && isLoadingOperationalFields
        ),
        ...validatePlsCreateLoad(
          {
            stops: normalizedRouteStops,
            rateData: data.rateData,
            specifications: data.specifications,
            accessorials: data.accessorials,
          },
          new Date()
        ),
      ];
      if (createErrors.length > 0) {
        applySubmitValidationErrors(createErrors);
        return;
      }
    }

    const duplicateStops =
      validateDuplicateRouteStopLocations(normalizedRouteStops);
    if (!duplicateStops.ok) {
      applySubmitValidationErrors([
        {
          path: `stops.${duplicateStops.pathIndex}.address.addressLine1`,
          message: duplicateStops.message,
        },
      ]);
      return;
    }

    for (let index = 0; index < normalizedRouteStops.length; index++) {
      const stop = normalizedRouteStops[index];

      if (isPlsPickupStopType(stop.stopType)) {
        if (!isValidPlsDateTimeWindow(stop.readyTime, stop.readyEndTime)) {
          applySubmitValidationErrors([
            {
              path: `stops.${index}.readyEndTime`,
              message:
                'Latest pickup time must be on or after the earliest time.',
            },
          ]);
          return;
        }
        continue;
      }

      if (
        isPlsDropoffStopType(stop.stopType) &&
        !isValidPlsDateTimeWindow(stop.mustDeliver, stop.readyEndTime)
      ) {
        applySubmitValidationErrors([
          {
            path: `stops.${index}.readyEndTime`,
            message:
              'Latest delivery time must be on or after the earliest time.',
          },
        ]);
        return;
      }
    }

    const monotonicStops =
      validatePlsMonotonicRouteStopTimes(normalizedRouteStops);
    if (!monotonicStops.ok) {
      const nextStop = normalizedRouteStops[monotonicStops.pathIndex];
      const path = (
        isPlsDropoffStopType(nextStop?.stopType)
          ? `stops.${monotonicStops.pathIndex}.mustDeliver`
          : `stops.${monotonicStops.pathIndex}.readyTime`
      ) as FieldPath<PlsFormValues>;
      applySubmitValidationErrors([
        {
          path,
          message: monotonicStops.message,
        },
      ]);
      return;
    }

    if (!hasCompletePlsCommodity(data.commodities)) {
      applySubmitValidationErrors([
        {
          path: 'commodities.0.description',
          message: 'At least one complete commodity is required.',
        },
      ]);
      return;
    }

    try {
      if (isCreateMode) {
        data.freightTrackingID = '';
        data.externalTMSID = '';
      }

      const loadPayload = buildPlsCreateLoadPayload(
        {
          ...data,
          // PLS accepts zoneless local wall-clock strings. Form Date values
          // already store those clock digits in UTC components, so applying
          // the browser offset here would change the time sent to PLS.
          stops: normalizedRouteStops,
          pickup: syncedEnds.pickup,
          consignee: syncedEnds.consignee,
          moreThanTwoStops: isPlsMultiStopRoute(normalizedRouteStops),
          ...(isCreateMode
            ? {}
            : {
                ID: load?.ID,
                freightTrackingID:
                  data.freightTrackingID || load?.freightTrackingID || '',
                externalTMSID: data.externalTMSID || load?.externalTMSID || '',
              }),
        },
        isCreateMode ? (tmsID ?? 0) : (load?.tmsID ?? tmsID ?? 0)
      );

      if (isCreateMode) {
        const reqData = withLoadBuildingSuggestionContext(
          {
            clickedSuggestion,
            threadId,
            emailId: emailId ?? 0,
            emailExternalId: threadItemId ?? '',
          },
          {
            load: loadPayload as unknown as Load,
            metadata: createLoadDateOnlyMetadata(
              syncedEnds.pickup.readyTime,
              syncedEnds.consignee.mustDeliver
            ),
          }
        );

        const res = await guardedCreateLoad(
          data.customer,
          reqData,
          plsData.customers ?? []
        );
        if (!res) {
          return;
        }

        const finishSuccessfulCreate = (createdLoad: Load) => {
          setSavedDraft({ key: draftContextKey, state: undefined });
          setBuiltLoad(createdLoad);
          FormStorageService.clearFormState(formStorageKey);
          if (isLoadBuildingSuggestionClicked && clickedSuggestion) {
            setCurrentState((prevState) => ({
              ...prevState,
              ...applyCompleteKitSuggestions(
                prevState,
                clickedSuggestion.id,
                SuggestionPipelines.LoadBuilding
              ),
            }));
          }
        };

        if (res.isOk()) {
          finishSuccessfulCreate(res.value.load);
        } else {
          const isWarning = res.error.level === 'warning';
          if (isWarning && res.error.load) {
            finishSuccessfulCreate(res.error.load);
          }
          const routeStops = formMethods.getValues('stops') ?? [];
          const plsFieldErrors = getPlsCreateLoadFieldErrors(
            res.error.response,
            {
              routeStopCount: routeStops.length,
              stops: routeStops,
            }
          );
          if (!isWarning && plsFieldErrors.length > 0) {
            applySubmitValidationErrors(plsFieldErrors);
            return;
          }
          toast({
            description: res.error.message || 'Error building load.',
            variant: isWarning ? 'warning' : 'destructive',
          });
        }
        return;
      }

      if (load?.ID == null) {
        toast({
          description: 'Load ID is required for updates.',
          variant: 'destructive',
        });
        return;
      }

      const res = await updateTMS(load.ID, {
        operation: TMSOperation.UpdateLoad,
        load: loadPayload as unknown as Load,
      });

      if (res.isOk()) {
        toast({
          description: res.value.message || 'Load updated successfully!',
          variant: 'success',
        });
        await acceptLoadViewSuggestion(
          clickedSuggestion ?? null,
          loadPayload as unknown as Load,
          setCurrentState,
          undefined,
          tmsName
        );
        if (res.value.load) {
          formMethods.reset(
            mapBuiltLoadToPlsFormValues(
              res.value.load,
              fieldDefinitions.map((definition) => definition.code)
            ),
            {
              keepDefaultValues: false,
            }
          );
        }
        await refetchLoadFromContext(invalidateLoad, res.value);
      } else {
        const isWarning = res.error.level === 'warning';
        const routeStops = formMethods.getValues('stops') ?? [];
        const plsFieldErrors = getPlsCreateLoadFieldErrors(res.error.response, {
          routeStopCount: routeStops.length,
          stops: routeStops,
        });
        if (!isWarning && plsFieldErrors.length > 0) {
          applySubmitValidationErrors(plsFieldErrors);
          return;
        }
        toast({
          description: res.error.message || 'Error updating load.',
          variant: isWarning ? 'warning' : 'destructive',
        });
        if (res.error.load && res.error.loadAttributes) {
          await refetchLoadFromContext(invalidateLoad, {
            load: res.error.load,
            loadAttributes: res.error.loadAttributes,
          });
        }
      }
    } catch {
      toast({
        description: isCreateMode
          ? 'Error building load.'
          : 'Error updating load.',
        variant: 'destructive',
      });
    } finally {
      setIsLoading(false);
    }
  };

  const onInvalid: SubmitErrorHandler<PlsFormValues> = async (errors) => {
    handleFormErrors(errors, {
      tmsName: TMS.PLS,
      maxLabelsToShow: DEFAULT_MAX_VALIDATION_TOAST_LABELS,
      activeTabs,
      setActiveTabs,
      toast,
      routeStopCount: (formMethods.getValues('stops') ?? []).length,
    });
  };

  const handleClearForm = () => {
    setSavedDraft({ key: draftContextKey, state: undefined });
    explicitlyClearedKeyRef.current = formStorageKey;
    setBuiltLoad(null);
    setCurrentState((prevState) => ({
      ...prevState,
      clickedSuggestion: null,
    }));
    formMethods.reset(initPlsLoadForm(), FORM_RESET_OPTIONS);
    formMethods.clearErrors();
    FormStorageService.clearFormState(formStorageKey);
  };

  const suggestedLoad = clickedSuggestion?.suggested as Undef<PlsFormValues>;
  const suggestedCustomerExternalTMSID = suggestedLoad?.customer?.externalTMSID;
  const suggestedBillToExternalTMSID = suggestedLoad?.billTo?.externalTMSID;
  const suggestedPickupExternalTMSID = suggestedLoad?.pickup?.externalTMSID;
  const suggestedConsigneeExternalTMSID =
    suggestedLoad?.consignee?.externalTMSID;

  return {
    formMethods,
    plsData,
    operationalFields: {
      fieldDefinitions,
      isLoading: isLoadingOperationalFields,
      error: operationalFieldsError,
    },
    referenceCatalogs,
    state: {
      activeTabs,
      setActiveTabs,
      expandAll,
      collapseAll,
      defaultView,
      setDefaultView,
      allTabs: ALL_PLS_TABS,
      isLoading,
      builtLoad,
      showAIHints,
      suggestedCustomerExternalTMSID,
      suggestedBillToExternalTMSID,
      suggestedPickupExternalTMSID,
      suggestedConsigneeExternalTMSID,
    },
    actions: {
      onSubmit,
      onInvalid,
      handleClearForm,
    },
  };
}
